import type { ComponentProps, ReactNode, RefObject } from 'react';
import { useRef } from 'react';
import { useIntl } from 'react-intl';

import type { ChatItem, UserMessageItem } from '../../../contracts/harness.types';
import type { DesktopControlLeaseSnapshot } from '../../../contracts/projects/desktop-control-approval.types';
import type { LiveDelegateStep } from '../../../data/projects/hooks/useProjectDesktopDelegation';
import type { LatestTodoPlan } from '../../../data/projects/timeline/agent-todo/computeLatestTodoPlan';
import {
  type ProjectItemRendererHooks,
  useProjectItemRenderer,
} from '../hooks/useProjectItemRenderer';
import type { ProjectItemRendererDeps } from '../timeline/renderProjectItem';
import { ProjectTurnTimeline } from './ProjectTurnTimeline';

export interface ProjectTimelineRendererOptions {
  desktopControlLease?: DesktopControlLeaseSnapshot | null;
  desktopTargetEnabled?: boolean;
  requireComputerUsePermissions?: ProjectItemRendererDeps['requireComputerUsePermissions'];
  resolvingApproval?: boolean;
  shouldAnimateUserMessage?: (item: UserMessageItem) => boolean;
  projectTitle?: string;
  routineRunWorkflowId?: string;
  /** True while the turn is running — keeps the Working block header live. */
  turnInFlight?: boolean;
  inlineReplyActions?: ProjectItemRendererDeps['inlineReplyActions'];
  loaderPhrases?: ProjectItemRendererDeps['loaderPhrases'];
}

export interface ProjectTimelineLayoutOptions {
  latestTurnMinHeight?: number | null;
  tailContent?: ReactNode;
  tailRef?: RefObject<HTMLDivElement>;
  renderCompletedTurnFooter?: ComponentProps<
    typeof ProjectTurnTimeline
  >['renderCompletedTurnFooter'];
}

export interface ProjectTimelineViewProps {
  agentRunId?: string | null;
  items: ChatItem[];
  latestTodoPlan: LatestTodoPlan | null;
  liveDelegateStep?: LiveDelegateStep;
  onOpenArtifact?: ProjectItemRendererDeps['onOpenArtifact'];
  onResolveApproval?: ProjectItemRendererDeps['onResolveApproval'];
  projectId: string;
  viewedDocument?: ProjectItemRendererDeps['viewedDocument'];
  rendererOptions?: ProjectTimelineRendererOptions;
  layout?: ProjectTimelineLayoutOptions;
  /** What the item renderer reads from the app, bound once at the composition point. */
  itemRendererHooks: ProjectItemRendererHooks;
}

const noopOpenArtifact: ProjectItemRendererDeps['onOpenArtifact'] = () => undefined;
const shouldAnimateUserMessage = () => true;

export const ProjectTimelineView = ({
  agentRunId,
  items,
  latestTodoPlan,
  liveDelegateStep,
  onOpenArtifact = noopOpenArtifact,
  onResolveApproval,
  projectId,
  viewedDocument,
  rendererOptions,
  layout,
  itemRendererHooks,
}: ProjectTimelineViewProps) => {
  const intl = useIntl();
  const fallbackTailRef = useRef<HTMLDivElement>(null);
  const renderItem = useProjectItemRenderer(
    {
      items,
      latestTodoPlan,
      agentRunId: agentRunId ?? undefined,
      projectId,
      onOpenArtifact,
      onResolveApproval,
      resolvingApproval: rendererOptions?.resolvingApproval ?? false,
      requireComputerUsePermissions: rendererOptions?.requireComputerUsePermissions,
      liveDelegateStep,
      desktopControlLease: rendererOptions?.desktopControlLease,
      desktopTargetEnabled: rendererOptions?.desktopTargetEnabled,
      intl,
      shouldAnimateUserMessage:
        rendererOptions?.shouldAnimateUserMessage ?? shouldAnimateUserMessage,
      projectTitle: rendererOptions?.projectTitle,
      routineRunWorkflowId: rendererOptions?.routineRunWorkflowId,
      turnInFlight: rendererOptions?.turnInFlight,
      viewedDocument,
      inlineReplyActions: rendererOptions?.inlineReplyActions,
      loaderPhrases: rendererOptions?.loaderPhrases,
    },
    itemRendererHooks
  );

  if (!renderItem) return <>{layout?.tailContent}</>;

  return (
    <ProjectTurnTimeline
      items={items}
      renderItem={renderItem}
      latestTurnMinHeight={layout?.latestTurnMinHeight}
      tailRef={layout?.tailRef ?? fallbackTailRef}
      tailContent={layout?.tailContent}
      renderCompletedTurnFooter={layout?.renderCompletedTurnFooter}
    />
  );
};
